import { Select } from '@chakra-ui/core';
import { useHireFormContext } from '../HireForm.context';
import { FieldSchema } from './formBuilderSchema';
import React from 'react';
import theme from '../../../../styles/customTheme';
import isEqual from 'lodash/isEqual';

interface ElementSelectProps {
  value: string;
  options: string[];
  onChange: (field: string) => void;
  onFocus?: (field: string) => void;
  disabled?: boolean;
  hidden?: boolean;
}
const ElementSelect = React.memo(function ({
  value,
  options = [],
  onChange,
  onFocus,
  disabled = false,
  hidden = false,
}: ElementSelectProps) {
  if (options.length === 0) return null;

  const fieldValueMap = {
    codeofconductandethics: 'Code of Conduct and Ethics',
    electronicmonitoringpolicy: 'Electronic Monitoring Policy',
    privacynoticeforemployees: 'Privacy Notice for Employees',
  } as Record<string, string>;

  const formatLabel = (field: string): string => {
    if (fieldValueMap[field]) return fieldValueMap[field];
    const words = field?.split('_')?.[0]?.match(/[A-Z]?[a-z]+|[0-9]+/g);
    return (
      words
        ?.map((word, index) => (index === 0 ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word.toLowerCase()))
        .join(' ') ?? ''
    );
  };

  const onChangeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onChange(value);
  };
  const isDisabled = disabled || options.length <= 1;
  if (hidden) return null;
  return (
    <Select
      variant="filled"
      size="xs"
      fontSize="xs"
      width="fit-content"
      value={value}
      onChange={onChangeHandler}
      disabled={isDisabled}
      onFocus={(e) => {
        const value = e.target.value;
        onFocus?.(value);
      }}
      data-testid="element-field-select"
      sx={{
        '&': {
          width: 'auto',
          padding: '0.30rem .5rem',
          paddingRight: '1.8rem',
          borderRadius: theme.radii.md,
        },
      }}
      iconColor={isDisabled ? theme.colors.gray['400'] : theme.colors.black}
    >
      {options?.map((option) => (
        <option key={option} value={option}>
          {formatLabel(option)}
        </option>
      ))}
    </Select>
  );
});

interface ElementSelectFieldProps {
  defaultValue: FieldSchema;
  options: string[];
  onChange: (field: string) => void;
  isAdvanced?: boolean;
}

const ElementSelectField = React.memo(
  ({ options, defaultValue, onChange, isAdvanced = false }: ElementSelectFieldProps) => {
    const { formSchema } = useHireFormContext();
    const [currentOptions, setCurrentOptions] = React.useState<string[]>(options);
    const value = defaultValue.field;

    React.useEffect(() => {
      setCurrentOptions(options);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options]);

    function handleOnChange(field: string) {
      onChange(field);
    }

    React.useEffect(() => {
      const baseFieldName = defaultValue.field?.split('_')[0];

      const newOptions = [...options];
      const optionIndex = newOptions.indexOf(baseFieldName);

      newOptions.splice(optionIndex, 1, defaultValue.field);
      const filteredOptions = getNewOptions(newOptions, defaultValue.field);

      if (!filteredOptions.includes(defaultValue.field)) {
        handleOnChange(filteredOptions[0]);
      }
      setCurrentOptions(filteredOptions);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (currentOptions.length < 1) return null;

    function getNewOptions(options: string[], currentField: string) {
      return filterOptions(
        formSchema?.flatMap((section) => section.fields as FieldSchema[]) ?? [],
        options,
        currentField?.split('_')[0],
      );
    }

    function filterOptions(fields: FieldSchema[], options: string[], currentField: string): string[] {
      const usedFields = new Set<string>();

      // Helper function to recursively collect all `field` values
      const collectFields = (fieldList: FieldSchema[]) => {
        fieldList.forEach((field) => {
          if (field.field !== currentField) usedFields.add(field.field?.split('_')[0]);
          if (field.followUpQuestionList) collectFields(field.followUpQuestionList);
        });
      };

      // Collect all `field` values from the JSON
      collectFields(fields);

      // Filter the options list
      return options.filter(
        (option) => !usedFields.has(option) || option.toLowerCase() === defaultValue.inputType?.toLowerCase(),
      );
    }

    return (
      <ElementSelect
        value={value}
        onChange={handleOnChange}
        options={currentOptions}
        onFocus={(field) => {
          const newOptions = getNewOptions(options, field);
          if (!newOptions.includes(field)) {
            handleOnChange(newOptions[0]);
          }
          setCurrentOptions(newOptions);
        }}
        hidden={isAdvanced || currentOptions.length <= 1}
      />
    );
  },
  (p, n) => {
    return isEqual(p.defaultValue, n.defaultValue) && p.onChange === n.onChange && isEqual(p.options, n.options);
  },
);

export default ElementSelectField;
